// src/Profile.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { NavLink, useNavigate } from "react-router-dom";

// Remove trailing slashes to avoid //me
const API_BASE = (process.env.REACT_APP_API_BASE || "/api").replace(/\/+$/, "");

function decodeJwtSafe(token) {
  try {
    const base64 = token.split(".")[1];
    if (!base64) return null;
    const normalized = base64
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(base64.length / 4) * 4, "=");
    const json = atob(normalized);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function isJwtExpired(decoded, skewMs = 15000) {
  if (!decoded?.exp) return false;
  const expMs = decoded.exp * 1000;
  return Date.now() + skewMs >= expMs;
}

export default function Profile() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const token = localStorage.getItem("token");
  const decoded = useMemo(() => (token ? decodeJwtSafe(token) : null), [token]);

  const signOut = useCallback(() => {
    const remembered = localStorage.getItem("rememberEmail");
    localStorage.clear();
    if (remembered) localStorage.setItem("rememberEmail", remembered);
    // notify other tabs/components
    window.dispatchEvent(new Event("user-updated"));
    navigate("/");
  }, [navigate]);

  const loadProfile = useCallback(
    async (signal) => {
      setErr("");
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/me`, {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
          signal,
        });

        if (res.status === 401) {
          setErr("Your session has expired. Please log in again.");
          signOut();
          return;
        }
        if (!res.ok) {
          throw new Error(`Failed to load profile (HTTP ${res.status})`);
        }

        const data = await res.json();
        setUser(data || null);

        // (Optional) keep a couple fields around for other pages
        if (data?.name) localStorage.setItem("name", data.name);
        if (data?.organization) localStorage.setItem("organization", data.organization);
        if (data?.email) localStorage.setItem("email", data.email);
        if (data?.profileImage) localStorage.setItem("profileImage", data.profileImage);

        // notify sidebar / other components in same tab + other tabs
        window.dispatchEvent(new Event("user-updated"));
      } catch (e) {
        if (e.name === "AbortError") return; // navigation/unmount
        // Network/CORS/mixed-content shows as TypeError in browsers
        const msg =
          e?.message?.includes("Failed to fetch")
            ? `Failed to fetch ${API_BASE}/me. Check network, CORS, or mixed-content (http↔https).`
            : e?.message || "Failed to load profile";
        setErr(msg);
      } finally {
        setLoading(false);
      }
    },
    [API_BASE, token, signOut]
  );

  useEffect(() => {
    if (!token) {
      signOut();
      return;
    }
    if (isJwtExpired(decoded)) {
      setErr("Your session has expired. Please log in again.");
      signOut();
      return;
    }

    const ctrl = new AbortController();
    loadProfile(ctrl.signal);
    return () => ctrl.abort();
  }, [token, decoded, signOut, loadProfile]);

  const expText = useMemo(() => {
    if (!decoded?.exp) return null;
    const d = new Date(decoded.exp * 1000);
    return d.toLocaleString();
  }, [decoded]);

  if (loading) return <div className="p-3">Loading profile…</div>;

  if (err) {
    return (
      <div className="container py-4">
        <div className="alert alert-danger d-flex justify-content-between align-items-center gap-3">
          <div className="small">{err}</div>
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-light btn-sm"
              onClick={() => {
                const ctrl = new AbortController();
                loadProfile(ctrl.signal);
              }}
            >
              Try Again
            </button>
            <button className="btn btn-light btn-sm" onClick={signOut}>
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="container py-4">
        <div className="alert alert-warning">No user data available.</div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="h4 mb-0">Account Details</h2>
        <NavLink to="/profile" className="list-group-item list-group-item-action">
          <i className="bi bi-person"></i> Profile
        </NavLink>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-6">
              <div className="form-text mb-1">Name</div>
              <div className="fw-semibold">{user.name || "-"}</div>
            </div>
            <div className="col-md-6">
              <div className="form-text mb-1">Email</div>
              <div className="fw-semibold">{user.email || "-"}</div>
            </div>
            <div className="col-md-6">
              <div className="form-text mb-1">Role</div>
              <div className="fw-semibold">{user.role || "-"}</div>
            </div>
            <div className="col-md-6">
              <div className="form-text mb-1">Organization</div>
              <div className="fw-semibold">{user.organization || "-"}</div>
            </div>
          </div>

          {expText && (
            <div className="mt-3 small text-muted">
              Session expires: <span className="fw-semibold">{expText}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
